import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    settings: {},                 // full settings object from backend
    globalReturnMultiplier: null,
    timer: 0,
    timerDisplay: "00:00",
    phase: "bidding", // bidding, autosubmit, hold
    roundNumber: 0,
    status: "running",
    bidPredictions: {}, // { "0": 5, "1": 12, ... }
    bidDetails: {}, // { "0": { count: 5, totalAmount: 2500 }, "1": { count: 12, totalAmount: 6000 } }
    totalAmount: 0,
    selectedWinner: null,
    isManualWinnerWindowOpen: false,
    lastTenWinners: [], // Persistent history
    loading: false,
    error: null,
};

const settingsSlice = createSlice({
    name: "lt1Settings",
    initialState,
    reducers: {
        fetchSettingsStart(state) {
            state.loading = true;
            state.error = null;
        },
        fetchSettingsSuccess(state, action) {
            state.loading = false;
            state.error = null;
            state.settings = action.payload;
            state.globalReturnMultiplier = action.payload.globalReturnMultiplier;

            // Initialize bid state if present in settings
            if (action.payload.bids) {
                state.bidPredictions = action.payload.bids;
            }
            if (action.payload.totalAmount !== undefined) {
                state.totalAmount = action.payload.totalAmount;
            }
            if (action.payload.numberTotals) {
                Object.entries(action.payload.numberTotals).forEach(([num, amount]) => {
                    const numStr = String(num);
                    state.bidDetails[numStr] = {
                        count: state.bidPredictions[numStr] || 0,
                        totalAmount: amount
                    };
                });
            }

            // Sync history from settings API
            if (action.payload.lastTenWinners && Array.isArray(action.payload.lastTenWinners)) {
                state.lastTenWinners = action.payload.lastTenWinners;
            }
        },
        fetchSettingsFailure(state, action) {
            state.loading = false;
            state.error = action.payload;
        },
        updateTimer(state, action) {
            const timeLeft = action.payload;
            state.timer = timeLeft;
            // Format timer as MM:SS
            const minutes = Math.floor(timeLeft / 60);
            const seconds = timeLeft % 60;
            state.timerDisplay = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
        },
        updateGameState(state, action) {
            const { timeLeft, phase, roundNumber, status } = action.payload;

            // Update timer
            if (timeLeft !== undefined) {
                state.timer = timeLeft;
                const minutes = Math.floor(timeLeft / 60);
                const seconds = timeLeft % 60;
                state.timerDisplay = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
            }

            // Update phase
            if (phase) {
                state.phase = phase;
            }

            // Update round number
            if (roundNumber !== undefined) {
                state.roundNumber = roundNumber;
            }

            // Update status
            if (status) {
                state.status = status;
            }

            // Manual winner window logic - Controlled by backend phase and socket events
            const phaseVal = state.phase;
            if (phaseVal === "autosubmit") {
                state.isManualWinnerWindowOpen = true;
            }
        },
        updateBidPredictions(state, action) {
            const bids = action.payload.bids || {};
            const isIncremental = action.payload.isIncremental;

            if (isIncremental) {
                // Add to existing counts
                Object.entries(bids).forEach(([num, count]) => {
                    const numStr = String(num);
                    state.bidPredictions[numStr] = (state.bidPredictions[numStr] || 0) + count;
                });

                // Also update the global total amount if it's there
                if (action.payload.totalAmount !== undefined) {
                    state.totalAmount = action.payload.totalAmount;
                }
            } else {
                // Replace all counts (full sync)
                state.bidPredictions = bids;
                state.totalAmount = action.payload.totalAmount || 0;
            }

            // Sync bidDetails with updated predictions and totals
            if (action.payload.numberTotals) {
                Object.entries(action.payload.numberTotals).forEach(([num, amount]) => {
                    const numStr = String(num);
                    state.bidDetails[numStr] = {
                        count: isIncremental
                            ? (state.bidDetails[numStr]?.count || 0) + (bids[numStr] || 0)
                            : (bids[numStr] || 0),
                        totalAmount: isIncremental
                            ? (state.bidDetails[numStr]?.totalAmount || 0) + amount
                            : amount
                    };
                });
            } else {
                // If no numberTotals, just update counts in existing bidDetails or create new ones
                Object.entries(state.bidPredictions).forEach(([num, count]) => {
                    const numStr = String(num);
                    if (!state.bidDetails[numStr]) {
                        state.bidDetails[numStr] = { count: 0, totalAmount: 0 };
                    }
                    state.bidDetails[numStr].count = count;
                });
            }
        },

        // NEW: Handle placeBid event
        handlePlaceBid(state, action) {
            const { number, amount } = action.payload;

            if (number === undefined || amount === undefined) {
                console.error('Invalid placeBid data:', action.payload);
                return;
            }

            const numStr = String(number);

            // Initialize bidDetails for this number if it doesn't exist
            if (!state.bidDetails[numStr]) {
                state.bidDetails[numStr] = {
                    count: 0,
                    totalAmount: 0
                };
            }

            // Update count and total amount for this number
            state.bidDetails[numStr] = {
                count: (state.bidDetails[numStr].count || 0) + 1,
                totalAmount: (state.bidDetails[numStr].totalAmount || 0) + amount
            };

            // Also update bidPredictions for backward compatibility
            state.bidPredictions[numStr] = (state.bidPredictions[numStr] || 0) + 1;

            // Update total amount
            state.totalAmount = (state.totalAmount || 0) + amount;
        },

        // NEW: Update bid details from initial data
        updateBidDetails(state, action) {
            const { number, details } = action.payload;

            if (number !== undefined && details) {
                const numStr = String(number);
                state.bidDetails[numStr] = {
                    count: details.count || 0,
                    totalAmount: details.totalAmount || 0
                };
            } else if (typeof action.payload === 'object') {
                // If payload is an object with all bid details
                state.bidDetails = { ...action.payload };
            }
        },

        // NEW: Reset bid details on new round
        resetBidDetails(state) {
            state.bidDetails = {};
        },

        setManualWinnerWindow(state, action) {
            state.isManualWinnerWindowOpen = action.payload;
        },
        setSelectedWinner(state, action) {
            state.selectedWinner = action.payload;
        },
        resetRound(state) {
            state.bidPredictions = {};
            state.bidDetails = {}; // Also reset bid details
            state.totalAmount = 0;
            state.selectedWinner = null;
            state.isManualWinnerWindowOpen = false;
        },
        updateHistory(state, action) {
            state.lastTenWinners = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
            state.loading = false;
        },
    },
});

export const {
    fetchSettingsStart,
    fetchSettingsSuccess,
    fetchSettingsFailure,
    updateTimer,
    updateGameState,
    updateBidPredictions,
    handlePlaceBid, // NEW: Export handlePlaceBid
    updateBidDetails, // NEW: Export updateBidDetails
    resetBidDetails, // NEW: Export resetBidDetails
    setManualWinnerWindow,
    setSelectedWinner,
    resetRound,
    updateHistory,
    setLoading,
    setError,
} = settingsSlice.actions;

export default settingsSlice.reducer;